import React, { FC, useState } from "react";
import { Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";

import { AiGoalType, GoalSuggestion, GoalSuggestionSlot, GoalTrafficLight } from "../../DTO";
import { AppUserApi } from "../../api";
import { useAppSelector } from "../../redux-config/hooks";
import { selectUser } from "../../redux-config/entities/user";
import { formatUserId } from "../../utils";

type Props = {
    goal: AiGoalType;
    show: boolean;
    onHide: () => void;
    onSaved: (updatedGoal: AiGoalType) => void;
    onSavedAndApproved: (goalId: number) => void;
};

const SLOT_LABELS: Record<GoalSuggestionSlot, string> = {
    priority: "Priority",
    support_habit: "Support Habit",
    stability_habit: "Stability Habit",
};
const COLORS: Record<GoalTrafficLight, string> = {
    red: "danger",
    amber: "warning",
    green: "success",
};
const DEFAULT_SUGGESTIONS: GoalSuggestion[] = [
    { slot: "priority", text: "", traffic_light: "red", focus_area: "general" },
    { slot: "support_habit", text: "", traffic_light: "amber", focus_area: "general" },
    { slot: "stability_habit", text: "", traffic_light: "green", focus_area: "general" },
];

export const EditSuggestionsModal: FC<Props> = ({ goal, show, onHide, onSaved, onSavedAndApproved }) => {
    const user = useAppSelector(selectUser);
    const [description, setDescription] = useState(goal.description ?? "");
    const [recommendedAction, setRecommendedAction] = useState(goal.recommendedAction ?? "");
    const [focusArea, setFocusArea] = useState(goal.focusArea ?? "");
    const [metricTarget, setMetricTarget] = useState(goal.metricTarget ?? "");
    const [reason, setReason] = useState(goal.reason ?? "");
    const [suggestions, setSuggestions] = useState<GoalSuggestion[]>(
        goal.suggestions?.length === 3
            ? goal.suggestions.map((item) => ({ ...item }))
            : DEFAULT_SUGGESTIONS.map((item) => ({ ...item })),
    );
    const [saving, setSaving] = useState(false);
    const [approving, setApproving] = useState(false);

    const updateSuggestion = (index: number, update: Partial<GoalSuggestion>) =>
        setSuggestions((current) => current.map((item, i) => i === index ? { ...item, ...update } : item));

    const save = async (approve: boolean) => {
        const empty = suggestions.find((item) => !item.text.trim());
        if (empty) {
            Swal.fire({ icon: "warning", text: `${SLOT_LABELS[empty.slot]} text cannot be empty.` });
            return;
        }
        approve ? setApproving(true) : setSaving(true);
        try {
            const updated = await AppUserApi.UpdateGoal(user, goal.id, {
                description, recommendedAction, focusArea, metricTarget, reason, suggestions,
            });
            if (approve) {
                await AppUserApi.ApprovePendingGoal(user, goal.id);
                onSavedAndApproved(goal.id);
            } else {
                onSaved(updated);
            }
            Swal.fire({ icon: "success", title: approve ? "Saved & Approved" : "Changes saved", timer: 1200, showConfirmButton: false });
            onHide();
        } catch (err) {
            Swal.fire({ icon: "error", text: String(err) });
        } finally {
            setSaving(false);
            setApproving(false);
        }
    };

    const scope = goal.type === "week" ? `Week ${goal.weekId ?? ""}` : `Day ${goal.dayId ?? ""}`;
    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Edit {goal.type === "week" ? "Weekly" : "Daily"} Goal
                    <small className="text-muted d-block mt-1" style={{ fontSize: "0.72rem" }}>
                        {scope} · User {formatUserId(goal.userId)} · {goal.status}
                    </small>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="goal-edit-overview-grid mb-4">
                    <Form.Group><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} /></Form.Group>
                    <Form.Group><Form.Label>Recommended action</Form.Label><Form.Control as="textarea" rows={2} value={recommendedAction} onChange={(e) => setRecommendedAction(e.target.value)} /></Form.Group>
                    <Form.Group><Form.Label>Focus area</Form.Label><Form.Control value={focusArea} onChange={(e) => setFocusArea(e.target.value)} /></Form.Group>
                    <Form.Group><Form.Label>Metric target</Form.Label><Form.Control value={metricTarget} onChange={(e) => setMetricTarget(e.target.value)} /></Form.Group>
                    <Form.Group className="goal-edit-reason"><Form.Label>Coach rationale</Form.Label><Form.Control value={reason} onChange={(e) => setReason(e.target.value)} /></Form.Group>
                </div>
                <h6 className="mb-3">Goal actions</h6>
                {suggestions.map((suggestion, index) => (
                    <div key={suggestion.slot} className="goal-edit-suggestion mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Badge bg={COLORS[suggestion.traffic_light]} text={suggestion.traffic_light === "amber" ? "dark" : undefined}>{suggestion.traffic_light.toUpperCase()}</Badge>
                            <strong>{SLOT_LABELS[suggestion.slot]}</strong>
                        </div>
                        <Form.Control value={suggestion.text} maxLength={200} onChange={(e) => updateSuggestion(index, { text: e.target.value })} />
                        <div className="d-flex gap-2 mt-2">
                            <Form.Select value={suggestion.traffic_light} onChange={(e) => updateSuggestion(index, { traffic_light: e.target.value as GoalTrafficLight })}>
                                <option value="red">Red</option><option value="amber">Amber</option><option value="green">Green</option>
                            </Form.Select>
                            <Form.Control value={suggestion.focus_area} placeholder="Focus area" onChange={(e) => updateSuggestion(index, { focus_area: e.target.value })} />
                        </div>
                    </div>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide} disabled={saving || approving}>Cancel</Button>
                <Button variant="primary" onClick={() => save(false)} disabled={saving || approving}>
                    {saving && <Spinner animation="border" size="sm" className="me-1" />}Save changes
                </Button>
                {goal.status === "pending" && goal.type === "week" && (
                    <Button variant="success" onClick={() => save(true)} disabled={saving || approving}>
                        {approving && <Spinner animation="border" size="sm" className="me-1" />}Save & Approve
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};
